import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
    PencilAltIcon,
    EllipsisVIcon,
    CubesIcon,
    TrashIcon,
    MapMarkerIcon,
    RouteIcon,
    BanIcon
} from '@patternfly/react-icons';
import { getCampaignTypeConfig } from './campaignTypeUtils';
import { TargetReachProgress } from './TargetReachProgress';
import { isActiveTargetReachStep } from './targetReachStepUtils';

export interface ICampaignCardProps {
    campaign: {
        campaignId: string;
        name: string;
        organizationName?: string;
        organizationId?: string;
        type?: string;
        campaignType?: string;
        status?: string;
        currentState?: 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'COMPLETED' | 'PARTIALLY_COMPLETE' | 'NEEDS_RUNS' | 'STOPPED_BY_OWNER' | 'ERROR' | string;
        generationStatus?: string;
        currentWorkflowStep?: string;
        ads?: any[];
        config?: {
            geoFence?: {
                address?: string;
                radius?: number;
                latitude?: number;
                longitude?: number;
            };
            drawRoutes?: {
                startingPoint?: {
                    address?: string;
                    latitude?: number;
                    longitude?: number;
                };
                waypoints?: { latitude: number; longitude: number; order: number }[];
                totalDistanceText?: string;
            };
        };
    };
    onEditClick?: (campaign: any) => void;
    onCardClick?: (campaign: any) => void;
    onDeleteClick?: (campaign: any) => void;
    onStopClick?: (campaign: any) => void;
}

const CampaignCard: React.FunctionComponent<ICampaignCardProps> = ({ campaign, onEditClick, onCardClick, onDeleteClick, onStopClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showStopConfirm, setShowStopConfirm] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return function() {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Generate a unique gradient based on campaign name for placeholder
    const getGradientFromName = (name: string) => {
        const gradients = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            'linear-gradient(135deg, #667eea 0%, #f093fb 100%)',
            'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)'
        ];
        // Simple hash from name to pick a gradient
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return gradients[Math.abs(hash) % gradients.length];
    };

    const placeholderGradient = getGradientFromName(campaign.name || 'Campaign');
    const campaignInitial = (campaign.name || 'C').charAt(0).toUpperCase();

    const getStatusColor = (state: string | undefined) => {
        const stateLower = state ? state.toLowerCase() : '';
        switch (stateLower) {
            case 'active':
            case 'running':
                return { bg: '#10b981', text: '#ffffff' };
            case 'paused':
                return { bg: '#f59e0b', text: '#ffffff' };
            case 'draft':
                return { bg: '#6b7280', text: '#ffffff' };
            case 'scheduled':
                return { bg: '#3b82f6', text: '#ffffff' };
            case 'completed':
            case 'ended':
                return { bg: '#8b5cf6', text: '#ffffff' };
            case 'partially_complete':
                return { bg: '#a855f7', text: '#ffffff' };
            case 'needs_runs':
                return { bg: '#d97706', text: '#ffffff' };
            case 'stopped_by_owner':
                return { bg: '#f97316', text: '#ffffff' };
            case 'error':
                return { bg: '#dc2626', text: '#ffffff' };
            default:
                return { bg: '#667eea', text: '#ffffff' };
        }
    };

    // Prefer currentState from API, fallback to status
    const displayState = campaign.currentState || campaign.status;
    const statusColors = getStatusColor(displayState);
    const adsCount = campaign.ads ? campaign.ads.length : 0;
    const isGeoFenceCampaign = (campaign.type || campaign.campaignType) === 'GeoFenceCampaign';
    const geoFenceData = campaign.config && campaign.config.geoFence ? campaign.config.geoFence : null;
    const isDrawRoutesCampaign = (campaign.type || campaign.campaignType) === 'DrawRoutesCampaign';
    const drawRoutesData = campaign.config && campaign.config.drawRoutes ? campaign.config.drawRoutes : null;
    const isGenerating = campaign.generationStatus === 'IN_PROGRESS';

    const styles = {
        cardContainer: {
            position: 'relative' as const,
            aspectRatio: '4 / 3',
            borderRadius: '12px',
            overflow: 'hidden',
            cursor: 'pointer',
            transform: isHovered ? 'scale(1.02)' : 'scale(1)',
            boxShadow: isGenerating
                ? undefined
                : isHovered
                    ? '0 20px 40px rgba(0, 0, 0, 0.2)'
                    : '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            animation: isGenerating ? 'campaignCardGlow 2s ease-in-out infinite' : undefined
        },
        backgroundGradient: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: placeholderGradient,
            transition: 'transform 0.3s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)'
        },
        placeholderInitial: {
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '80px',
            fontWeight: 800,
            color: 'rgba(255, 255, 255, 0.25)',
            textTransform: 'uppercase' as const,
            userSelect: 'none' as const,
            pointerEvents: 'none' as const
        },
        gradientOverlay: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.7) 100%)',
            transition: 'background 0.3s ease'
        },
        content: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column' as const,
            justifyContent: 'space-between',
            padding: '16px',
            boxSizing: 'border-box' as const
        },
        topRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'relative' as const,
            zIndex: 2
        },
        badgeContainer: {
            display: 'flex',
            gap: '6px',
            alignItems: 'center'
        },
        statusBadge: {
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px'
        },
        typeBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'capitalize' as const,
            backdropFilter: 'blur(4px)',
            whiteSpace: 'nowrap' as const
        },
        actionButtons: {
            display: 'flex',
            gap: '8px',
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.3s ease'
        },
        actionButton: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#374151',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        },
        menuContainer: {
            position: 'relative' as const
        },
        dropdownMenu: {
            position: 'absolute' as const,
            top: '100%',
            right: 0,
            marginTop: '4px',
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            zIndex: 100,
            minWidth: '140px'
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            transition: 'background 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const
        },
        menuItemDanger: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#dc2626',
            cursor: 'pointer',
            transition: 'background 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const
        },
        bottomContent: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '4px'
        },
        campaignName: {
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: 700,
            margin: 0,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        },
        organizationName: {
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: '13px',
            fontWeight: 500,
            margin: 0
        },
        metaInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginTop: '8px',
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translateY(0)' : 'translateY(10px)',
            transition: 'all 0.3s ease'
        },
        metaItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'rgba(255, 255, 255, 0.75)',
            fontSize: '12px'
        },
        // Delete confirmation modal styles
        confirmOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        },
        confirmModal: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        },
        confirmTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 8px 0'
        },
        confirmText: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 20px 0',
            lineHeight: 1.5
        },
        confirmButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        },
        cancelButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 500,
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            background: '#ffffff',
            color: '#374151',
            cursor: 'pointer'
        },
        deleteButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: '#dc2626',
            color: '#ffffff',
            cursor: 'pointer'
        },
        stopButton: {
            backgroundColor: '#f97316',
            border: 'none',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#ffffff',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        },
        confirmStopButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: '#f97316',
            color: '#ffffff',
            cursor: 'pointer'
        }
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        if (onEditClick) {
            onEditClick(campaign);
        }
    };

    const handleCardClick = () => {
        if (onCardClick) {
            onCardClick(campaign);
        }
    };

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
        if (onDeleteClick) {
            onDeleteClick(campaign);
        }
    };

    const handleCancelDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
    };

    const handleStopClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowStopConfirm(true);
    };

    const handleConfirmStop = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowStopConfirm(false);
        if (onStopClick) {
            onStopClick(campaign);
        }
    };

    const handleCancelStop = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowStopConfirm(false);
    };

    // Check campaign state for button visibility
    const isDraft = (campaign.currentState || '').toUpperCase() === 'DRAFT';
    const isActive = (campaign.currentState || '').toUpperCase() === 'ACTIVE';

    return (
        <>
            {isGenerating && (
                <style>{`
                    @keyframes campaignCardGlow {
                        0%, 100% {
                            box-shadow: 0 0 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2), 0 0 60px rgba(139, 92, 246, 0.1);
                        }
                        50% {
                            box-shadow: 0 0 20px rgba(139, 92, 246, 0.6), 0 0 45px rgba(139, 92, 246, 0.35), 0 0 80px rgba(139, 92, 246, 0.15);
                        }
                    }
                `}</style>
            )}
            <div
                style={styles.cardContainer}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => { setIsHovered(false); setShowMenu(false); }}
                onClick={handleCardClick}
            >
                {/* Background Gradient */}
                <div style={styles.backgroundGradient} />

                {/* Large Initial Letter */}
                <div style={styles.placeholderInitial}>{campaignInitial}</div>

                {/* Gradient Overlay */}
                <div style={styles.gradientOverlay} />

                {/* Content */}
                <div style={styles.content}>
                    {/* Top Row - Status Badge, Type & Action Buttons */}
                    <div style={styles.topRow}>
                        <div style={styles.badgeContainer}>
                            <span style={styles.statusBadge}>
                                {displayState === 'STOPPED_BY_OWNER' ? 'Stopped' : displayState === 'NEEDS_RUNS' ? 'Needs runs' : displayState || 'Draft'}
                            </span>
                            {(campaign.campaignType || campaign.type) && (() => {
                                const typeConfig = getCampaignTypeConfig(campaign.campaignType || campaign.type);
                                const TypeIcon = typeConfig.icon;
                                return (
                                    <span style={{
                                        ...styles.typeBadge,
                                        backgroundColor: typeConfig.color.bg,
                                        color: typeConfig.color.text
                                    }}>
                                        <TypeIcon style={{ width: 12, height: 12 }} />
                                        {typeConfig.label}
                                    </span>
                                );
                            })()}
                            {campaign.generationStatus && campaign.generationStatus !== 'COMPLETED' && (() => {
                                const genStatusMap: Record<string, { label: string; bg: string; text: string }> = {
                                    PENDING: { label: 'Generation Pending', bg: '#f59e0b', text: '#ffffff' },
                                    IN_PROGRESS: { label: 'Generating...', bg: '#8b5cf6', text: '#ffffff' },
                                    FAILED: { label: 'Generation Failed', bg: '#ef4444', text: '#ffffff' }
                                };
                                const gs = genStatusMap[campaign.generationStatus];
                                if (!gs) return null;
                                return (
                                    <span style={{
                                        ...styles.typeBadge,
                                        backgroundColor: gs.bg,
                                        color: gs.text
                                    }}>
                                        {gs.label}
                                    </span>
                                );
                            })()}
                        </div>
                        <div style={styles.actionButtons}>
                            {/* DRAFT: Show edit button + menu with edit/delete */}
                            {isDraft && (
                                <>
                                    <button
                                        style={styles.actionButton}
                                        onClick={handleEditClick}
                                        title="Edit"
                                    >
                                        <PencilAltIcon />
                                    </button>
                                    <div style={styles.menuContainer} ref={menuRef}>
                                        <button
                                            style={styles.actionButton}
                                            onClick={handleMenuClick}
                                            title="More options"
                                        >
                                            <EllipsisVIcon />
                                        </button>
                                        {showMenu && (
                                            <div style={styles.dropdownMenu}>
                                                <button
                                                    style={styles.menuItem}
                                                    onClick={handleEditClick}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                                >
                                                    <PencilAltIcon /> Edit
                                                </button>
                                                <button
                                                    style={styles.menuItemDanger}
                                                    onClick={handleDeleteClick}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                                >
                                                    <TrashIcon /> Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            {/* ACTIVE: Show stop button only */}
                            {isActive && (
                                <button
                                    style={styles.stopButton}
                                    onClick={handleStopClick}
                                    title="End Campaign"
                                >
                                    <BanIcon />
                                </button>
                            )}
                            {/* Other states: No action buttons */}
                        </div>
                    </div>

                    {/* Bottom Content - Name, Organization, Meta Info */}
                    <div style={styles.bottomContent}>
                        {isGenerating && campaign.currentWorkflowStep && isActiveTargetReachStep(campaign.currentWorkflowStep) && (
                            <TargetReachProgress currentStep={campaign.currentWorkflowStep} />
                        )}
                        <h3 style={styles.campaignName}>{campaign.name}</h3>
                        <p style={styles.organizationName}>
                            {campaign.organizationName || `Organization ${campaign.organizationId || ''}`}
                        </p>
                        <div style={styles.metaInfo}>
                            <div style={styles.metaItem}>
                                <CubesIcon />
                                <span>{adsCount} ad{adsCount !== 1 ? 's' : ''}</span>
                            </div>
                            {isGeoFenceCampaign && geoFenceData && geoFenceData.address && (
                                <div style={styles.metaItem}>
                                    <MapMarkerIcon />
                                    <span style={{
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {geoFenceData.address}
                                    </span>
                                </div>
                            )}
                            {isDrawRoutesCampaign && drawRoutesData && drawRoutesData.startingPoint && (
                                <div style={styles.metaItem}>
                                    <RouteIcon />
                                    <span style={{
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {drawRoutesData.startingPoint.address || 'Route'}
                                        {drawRoutesData.totalDistanceText && ` (${drawRoutesData.totalDistanceText})`}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div style={styles.confirmOverlay} onClick={handleCancelDelete}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>Delete Campaign</h3>
                        <p style={styles.confirmText}>
                            Are you sure you want to delete "{campaign.name}"? This action cannot be undone.
                        </p>
                        <div style={styles.confirmButtons}>
                            <button style={styles.cancelButton} onClick={handleCancelDelete}>
                                Cancel
                            </button>
                            <button style={styles.deleteButton} onClick={handleConfirmDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* End Campaign Confirmation Modal */}
            {showStopConfirm && (
                <div style={styles.confirmOverlay} onClick={handleCancelStop}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>End campaign</h3>
                        <p style={styles.confirmText}>
                            Are you sure you want to end "{campaign.name}"? This finalizes the campaign. Any runs that haven't started yet are cancelled and refunded; runs already in progress are stopped.
                        </p>
                        <div style={styles.confirmButtons}>
                            <button style={styles.cancelButton} onClick={handleCancelStop}>
                                Cancel
                            </button>
                            <button style={styles.confirmStopButton} onClick={handleConfirmStop}>
                                End campaign
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export { CampaignCard };
